import React, { useState } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import {
  Logout,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { brandTokens } from "../theme";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleLogout = () => {
    logout();
    handleClose();
  };

  const getInitials = (first: string, last: string) =>
    `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: brandTokens.navy,
        borderBottom: "1px solid rgba(245,158,11,0.15)",
        backgroundImage: `linear-gradient(135deg, ${brandTokens.navyLight} 0%, ${brandTokens.navy} 100%)`,
      }}
    >
      <Toolbar
        sx={{
          minHeight: "64px !important",
          px: 2,
          display: "flex",
          gap: 2,
        }}
      >
        {/* Logo / Brand */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>

          <img
            src={`${API_URL}/logotypes/logo.png`}
            alt="Geva Logistics"
            style={{
              maxHeight: 32,
              width: "auto",
              objectFit: "contain",
              opacity: 0.9,
            }}
            onError={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              if (!img.src.includes("/logo.png") || img.src.includes("logotypes")) {
                img.src = "/logo.png";
              } else {
                img.style.display = "none";
              }
            }}
          />
        </Box>


        <Box sx={{ flexGrow: 1 }} />

        {/* Driver badge + menu */}
        {user && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label="Driver"
              size="small"
              sx={{
                bgcolor: "rgba(245,158,11,0.15)",
                color: brandTokens.amber,
                fontWeight: 700,
                fontSize: "0.7rem",
                height: 22,
                border: "1px solid rgba(245,158,11,0.3)",
              }}
            />
            <Box
              sx={{
                display: { xs: "none", sm: "block" },
                textAlign: "right",
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: "#fff", lineHeight: 1.2 }}
              >
                {user.first_name} {user.last_name}
              </Typography>
            </Box>

            <IconButton
              onClick={handleMenu}
              sx={{
                p: 0.5,
                borderRadius: "10px",
                border: "1.5px solid rgba(255,255,255,0.12)",
                transition: "all 0.2s",
                "&:hover": {
                  border: "1.5px solid rgba(245,158,11,0.5)",
                  bgcolor: "rgba(245,158,11,0.08)",
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: brandTokens.amber,
                  color: brandTokens.navy,
                  width: 32,
                  height: 32,
                  fontSize: "0.78rem",
                  fontWeight: 700,
                }}
              >
                {getInitials(user.first_name, user.last_name)}
              </Avatar>
              <KeyboardArrowDown
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  fontSize: 15,
                  ml: 0.2,
                  transform: anchorEl ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 180,
                  borderRadius: "12px",
                  border: "1px solid #E2E8F0",
                  boxShadow: "0 8px 30px rgba(15,27,45,0.15)",
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" fontWeight={700}>
                  {user.first_name} {user.last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={handleLogout}
                sx={{
                  py: 1,
                  color: "error.main",
                  mx: 0.5,
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "error.light" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Logout fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>
                    Sign Out
                  </Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
